import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowLeft, ChevronRight, Gauge, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";

/**
 * Universal renderer for programmatic SEO pages.
 * Reads `location.pathname`, hits /api/seo/pages/{slug} to fetch pre-generated content.
 * If page is still generating, shows a graceful loading state.
 */
export default function SeoPage() {
    const params = useParams();
    const [doc, setDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    const slug = React.useMemo(() => {
        const p = window.location.pathname;
        // /research/{platform}/{trim}/{year} => research-{platform}-{trim}-{year}
        // /find/{trim}/{loc} => find-{trim}-{loc}
        // /compare/{a}-vs-{b} => compare-{a}-vs-{b}
        // /buy/{trim}/{year} => buy-{trim}-{year}
        // /trim/{slug} => trim-{slug}
        // /platform/{slug} => platform-{slug}
        const parts = p.replace(/^\//, "").replace(/\/$/, "").split("/");
        if (parts.length === 0) return "";
        if (parts[0] === "compare") return `compare-${parts.slice(1).join("-")}`;
        return parts.join("-");
    }, [params]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/seo/pages/${slug}`);
                setDoc(data);
            } catch (err) {
                setNotFound(true);
            } finally {
                setLoading(false);
            }
        })();
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-screen bg-onyx-950 text-white p-8">
                <div className="max-w-3xl mx-auto space-y-4">
                    <div className="h-12 brain-panel animate-pulse" />
                    <div className="h-8 brain-panel animate-pulse w-3/4" />
                    <div className="h-64 brain-panel animate-pulse mt-8" />
                </div>
            </div>
        );
    }

    if (notFound) {
        return (
            <div className="min-h-screen bg-onyx-950 text-white flex flex-col items-center justify-center p-8">
                <Gauge className="h-16 w-16 text-hellcat text-glow-red" />
                <h1 className="mt-4 text-3xl" style={{ fontFamily: "'Alfa Slab One', 'Ultra', serif", fontWeight: 400 }}>Page not found</h1>
                <Link to="/marketplace" className="mt-6 text-hellcat label-caps hover:underline">← Back to Marketplace</Link>
            </div>
        );
    }

    if (doc?.generating || (doc && !doc.html)) {
        return (
            <div className="min-h-screen bg-onyx-950 text-white flex flex-col items-center justify-center p-8">
                <div className="brain-mono text-hellcat text-xs mb-3 tracking-widest">// REMI IS WRITING THIS PAGE</div>
                <h1 className="text-3xl text-center" style={{ fontFamily: "'Alfa Slab One', 'Ultra', serif", fontWeight: 400 }}>
                    {doc?.title || "Loading"}
                </h1>
                <p className="mt-3 text-onyx-500 text-sm text-center max-w-md">
                    This page is being written by Remi right now. Check back in a minute. Meanwhile, browse verified listings.
                </p>
                <Link to="/marketplace" className="mt-6 btn-red px-5 py-2 label-caps" style={{ borderRadius: 2 }}>Browse Listings</Link>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-onyx-950 text-white brain-noise">
            <Helmet>
                <title>{doc.title}</title>
                <meta name="description" content={doc.excerpt || ""} />
                <link rel="canonical" href={`${process.env.REACT_APP_BACKEND_URL}${doc.path}`} />
            </Helmet>

            <header
                className="border-b border-onyx-700 sticky top-0 z-40 backdrop-blur-md"
                style={{ background: "rgba(5,5,5,0.85)" }}
            >
                <div className="max-w-5xl mx-auto px-5 py-3 flex items-center justify-between">
                    <Logo size="sm" />
                    <Link to="/marketplace" className="label-caps text-onyx-500 hover:text-hellcat flex items-center gap-1 transition-colors">
                        <ArrowLeft className="h-4 w-4" /> Marketplace
                    </Link>
                </div>
            </header>

            <motion.article
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="max-w-3xl mx-auto px-5 py-12"
            >
                <div className="brain-mono text-[10px] text-hellcat tracking-widest mb-3">// {doc.kind?.replace(/_/g, " ")}</div>
                <h1
                    className="text-4xl md:text-5xl leading-[1.05] text-white text-glow-red"
                    style={{ fontFamily: "'Alfa Slab One', 'Ultra', serif", fontWeight: 400 }}
                    data-testid="seo-page-h1"
                >
                    {doc.h1 || doc.title}
                </h1>

                {doc.excerpt && (
                    <p className="mt-5 text-lg text-onyx-300 leading-relaxed">{doc.excerpt}</p>
                )}

                <div
                    className="mt-8 prose prose-invert max-w-none prose-p:text-onyx-300 prose-p:leading-relaxed prose-headings:font-serif prose-headings:text-white prose-a:text-hellcat prose-strong:text-white prose-ul:text-onyx-300"
                    dangerouslySetInnerHTML={{ __html: doc.html || "" }}
                />

                {doc.faq && doc.faq.length > 0 && (
                    <section className="mt-14 border-t border-onyx-700 pt-8">
                        <div className="label-caps mb-2">// FAQ</div>
                        <h2 className="text-2xl text-white mb-6" style={{ fontFamily: "'Alfa Slab One', 'Ultra', serif", fontWeight: 400 }}>
                            Questions the Crew Asks
                        </h2>
                        <div className="space-y-4">
                            {doc.faq.map((f, i) => (
                                <div key={i} className="brain-panel-elev p-4">
                                    <div className="text-white font-semibold">{f.q}</div>
                                    <div className="mt-2 text-sm text-onyx-400 leading-relaxed">{f.a}</div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                <section className="mt-14 brain-panel-elev p-6">
                    <div className="label-caps text-hellcat mb-2">// Next stop</div>
                    <h3 className="text-2xl text-white mb-3" style={{ fontFamily: "'Alfa Slab One', 'Ultra', serif", fontWeight: 400 }}>
                        Browse verified listings
                    </h3>
                    <p className="text-onyx-400 text-sm mb-5">
                        Real inventory from dealers, private sellers, and aggregated sources. Filtered to SRT / R/T only.
                    </p>
                    <div className="flex gap-3 flex-wrap">
                        <Link to="/marketplace" className="btn-red px-5 py-2 label-caps flex items-center gap-2" style={{ borderRadius: 2 }}>
                            Marketplace <ChevronRight className="h-4 w-4" />
                        </Link>
                        <Link to="/the-ladder" className="btn-ghost px-5 py-2 label-caps" style={{ borderRadius: 2 }}>
                            The Ladder Blog
                        </Link>
                    </div>
                </section>
            </motion.article>

            <footer className="border-t border-onyx-700 py-8 text-center label-caps">
                <Link to="/" className="text-hellcat hover:underline">← SRT Finder Home</Link>
            </footer>
        </div>
    );
}
